import { purchaseRequestContractLimits as limits } from './contractLimits'
import type { ColumnRole, ImportRowIssue, ImportRowNote } from '../utils/requestItemsImport'
import { pluralizeRu } from '../utils/pluralizeRu'
import type { RussianPluralForms } from '../utils/pluralizeRu'

const ITEM_FORMS: RussianPluralForms = ['позиция', 'позиции', 'позиций']
const ITEM_ACCUSATIVE_FORMS: RussianPluralForms = ['позицию', 'позиции', 'позиций']
const ROW_FORMS: RussianPluralForms = ['строка', 'строки', 'строк']
const ROW_GENITIVE_FORMS: RussianPluralForms = ['строки', 'строк', 'строк']
const ROW_DATIVE_FORMS: RussianPluralForms = ['строке', 'строкам', 'строкам']
const COLUMN_FORMS: RussianPluralForms = ['колонка', 'колонки', 'колонок']

const formatNumber = (value: number): string => value.toLocaleString('ru-RU')

export const requestItemsImportUiText = {
  openAction: 'Вставить из таблицы',
  emptyStateHint: 'Длинный список быстрее скопировать из Excel, Word или Google Таблиц.',
  dialogTitle: 'Вставить позиции из таблицы',
  steps: ['Вставка', 'Колонки', 'Проверка по каталогу'],
  back: 'Назад',
  cancel: 'Отмена',
  next: 'Далее',
  unitsLoading: 'Загружаем справочник единиц измерения…',
  unitsFailed: 'Не удалось загрузить справочник единиц измерения, без него строки не проверить. Закройте окно и обновите страницу.',
  closeConfirmTitle: 'Закрыть без добавления?',
  closeConfirmDescription: 'Вставленная таблица и исправления на шагах не сохранятся.',
  closeConfirmAction: 'Закрыть',
  closeConfirmCancel: 'Вернуться',

  pasteAreaLabel: 'Область вставки',
  pasteTitle: 'Скопируйте строки таблицы и нажмите Ctrl+V',
  pasteHint: 'На Mac — ⌘V. Подходят Excel, Word, Google Таблицы и таблицы из писем.',
  pasteReplaceHint: 'Чтобы заменить её, скопируйте другую таблицу и нажмите Ctrl+V.',
  pasteNothingRecognized: 'Не удалось распознать строки. Скопируйте ячейки таблицы или строки текста и вставьте ещё раз.',
  fieldsTitle: 'Что берём из таблицы',
  fieldsCaption: 'Порядок колонок и заголовки могут быть любыми. Лишние колонки можно не выделять: на следующем шаге они отключаются.',
  plainTextTitle: 'Можно и обычным текстом',
  plainTextExample: 'Кабель ВВГнг 3×2,5 — 300 м\nВыключатель ВА47-29 16А — 24 шт',
  plainTextCaption: 'Одна позиция на строку, количество и единица в конце строки.',

  hasHeaderLabel: 'Первая строка — заголовки',
  headerRowNote: 'Первая строка — заголовки, в заявку она не попадёт.',
  textSourceNote: 'Строки прочитаны как обычный текст: название, в конце количество и единица.',
  requiredRolesChosen: 'Название и количество выбраны, можно продолжать.',
  noDataRows: 'Под заголовками нет строк. Снимите флажок «Первая строка — заголовки» или вставьте таблицу заново.',

  checkLoading: 'Сверяем названия с каталогом…',
  retry: 'Повторить',
  summaryLabel: 'Итоги проверки',
  readyTile: 'готовы к добавлению',
  withoutProductTile: 'без товара из каталога',
  withErrorsTile: 'с ошибкой в значении',
  viewLabel: 'Какие строки показать',
  matchCaption: 'Сопоставление идёт по названию: точное совпадение подставляется само.',
  listLabel: 'Строки из таблицы',
  nameColumn: 'Название из таблицы и товар в каталоге',
  quantityColumn: 'Количество',
  priceColumn: 'Цена',
  nothingNeedsAttention: 'Все строки готовы к добавлению.',
  noRowsLeft: 'Все строки исключены. Вернитесь назад или вставьте таблицу заново.',
  noNamedRows: 'В колонке «Название» нет ни одного значения. Вернитесь назад и выберите другую колонку.',
  foundInCatalog: 'Товар найден в каталоге',
  productWithoutUnit: 'в карточке товара единица не указана',
  similar: 'Похожие:',
  noSimilar: 'Похожих в каталоге нет',
  findInCatalog: 'Найти в каталоге',
  createProduct: 'Создать товар',
  changeProduct: 'Изменить',
  unlinkedRowNote: 'Позиция попадёт в заявку с отметкой',
  unitFromProductCard: 'единица из карточки товара',
  cancelSearch: 'Отмена',
  searchNoOptions: 'Ничего не найдено',
  searchLoading: 'Поиск...',
  unitPlaceholder: 'Единица',
  bulkUnitHint: 'Единица из карточки выбранного товара заменит указанную здесь.',
  bulkUnitLabel: 'Единица для всех',
  bulkUnitApply: 'Применить',
  checkFooter: 'Строки без товара из каталога попадут в заявку с отметкой, их можно исправить позже в форме. Строки с ошибкой в значении добавятся только после исправления.',

  createDialogTitle: 'Создать товар в каталоге',
  createNameLabel: 'Название товара',
  createNameHint: 'Добавьте обозначение или размер, чтобы товар отличался от похожих',
  createUnitLabel: 'Единица',
  createCategoryLabel: 'Категория',
  createNoCategory: 'Без категории',
  createCategoryFromRequest: 'Из заявки, можно изменить',
  createSubmit: 'Создать и выбрать',
  createFailed: 'Не удалось создать товар.',
  similarLoading: 'Ищем похожие товары',
  similarFailed: 'Не удалось проверить похожие товары. Товар можно создать и без проверки.',
  similarNone: 'В каталоге нет товара с похожим названием',
  similarFound: 'Похожие товары уже есть в каталоге. Проверьте, нет ли нужного',
  similarChoose: 'Выбрать',
  duplicateFound: 'Товар с таким названием уже есть в каталоге',
  duplicateChoose: 'Выбрать его',
  checkFailed: 'Не удалось сверить названия с каталогом.',
  invalidMatchAnswer: 'Сервер вернул неполный ответ при сверке с каталогом. Повторите попытку.',

  bannerUndo: 'Отменить вставку',
  bannerDismiss: 'Скрыть сообщение о вставке',
  filterLabel: 'Какие позиции показать',
  filterAll: 'Все',
  filterNeedsFix: 'Нужно исправить'
} as const

export const importColumnRoleLabels: Readonly<Record<ColumnRole, string>> = {
  name: 'Название',
  designation: 'Обозначение',
  quantity: 'Количество',
  unit: 'Ед. изм.',
  price: 'Цена',
  supplier: 'Поставщик',
  explanation: 'Пояснение',
  skip: 'Не брать'
}

export const importColumnRoleOrder: readonly ColumnRole[] = ['name', 'designation', 'quantity', 'unit', 'price', 'supplier', 'explanation', 'skip']

export const importRowNoteText: Readonly<Record<ImportRowNote, string>> = {
  supplierTruncated: `Поставщик обрезан до ${limits.itemSupplierMaxLength} символов`,
  explanationTruncated: `Пояснение обрезано до ${limits.itemExplanationMaxLength} символов`
}

const QUANTITY_RULE = `Нужно целое число от ${formatNumber(limits.itemQuantityMin)} до ${formatNumber(limits.itemQuantityMax)}`

export function formatImportRowIssue(issue: ImportRowIssue, unitText: string): string {
  switch (issue) {
    case 'nameTooLong':
      return `Название длиннее ${limits.itemNameMaxLength} символов. Такую строку можно только исключить`
    case 'quantityMissing':
      return `Укажите количество: целое число от ${formatNumber(limits.itemQuantityMin)} до ${formatNumber(limits.itemQuantityMax)}`
    case 'quantityInvalid':
    case 'quantityFractional':
    case 'quantityOutOfRange':
      return QUANTITY_RULE
    case 'unitMissing':
      return 'Нет единицы измерения'
    case 'unitUnknown':
      return `Единицы «${unitText}» нет в справочнике`
    case 'priceInvalid':
      return 'Цена должна быть числом. Исправьте или очистите поле'
    case 'priceOutOfRange':
      return `Цена от ${formatNumber(limits.itemPriceMin)} до ${formatNumber(limits.itemPriceMax)}`
  }
}

export function formatColumnRoleSelectLabel(columnNumber: number): string {
  return `Назначение колонки ${columnNumber}`
}

export function formatPastedRowsSummary(rowCount: number, columnCount: number): string {
  const verb = pluralizeRu(rowCount, ['Распознана', 'Распознано', 'Распознано'])
  return `${verb} ${rowCount} ${pluralizeRu(rowCount, ROW_FORMS)} и ${columnCount} ${pluralizeRu(columnCount, COLUMN_FORMS)}.`
}

export function formatPasteReady(rowCount: number): string {
  return `Таблица вставлена: ${rowCount} ${pluralizeRu(rowCount, ROW_FORMS)}.`
}

export function formatTooManyRows(count: number, limit: number): string {
  return `Вставлено ${formatNumber(count)} ${pluralizeRu(count, ROW_FORMS)}, а за один раз можно добавить не больше ${formatNumber(limit)}. Разделите таблицу на части.`
}

export function formatPreviewCaption(shown: number, total: number): string {
  return `Показаны первые ${shown} ${pluralizeRu(shown, ROW_GENITIVE_FORMS)} из ${total}`
}

export function formatMoreRows(count: number): string {
  return `и ещё ${count} ${pluralizeRu(count, ROW_FORMS)}`
}

export function formatMissingRoles(roles: readonly ColumnRole[]): string {
  const names = roles.map((role) => `«${importColumnRoleLabels[role]}»`).join(' и ')
  return roles.length === 1
    ? `Выберите колонку ${names}: без неё позиции не добавить.`
    : `Выберите колонки ${names}: без них позиции не добавить.`
}

export function formatSkippedEmptyRows(count: number): string {
  return `Пропущено пустых строк: ${count}`
}

export function formatAllRowsView(count: number): string {
  return `Все ${count}`
}

export function formatAttentionRowsView(count: number): string {
  return `Требуют внимания ${count}`
}

export function formatNeedsFixFilter(count: number): string {
  return `${requestItemsImportUiText.filterNeedsFix} ${count}`
}

export function formatAllItemsFilter(count: number): string {
  return `${requestItemsImportUiText.filterAll} ${count}`
}

export function formatAddItemsAction(count: number): string {
  return `Добавить ${count} ${pluralizeRu(count, ITEM_ACCUSATIVE_FORMS)}`
}

export function formatChosenProduct(name: string): string {
  return `Выбран товар: ${name}`
}

export function formatCreatedProduct(name: string): string {
  return `Создан товар: ${name}`
}

export function formatRowsWithoutUnit(count: number, hasUnitColumn: boolean): string {
  const rows = `${count} ${pluralizeRu(count, ROW_FORMS)}`
  return hasUnitColumn ? `${rows} без единицы измерения` : `В таблице нет единиц измерения: ${rows} без единицы`
}

export function formatUnitAppliedToRows(unit: string, count: number): string {
  return `Единица «${unit}» применена к ${count} ${pluralizeRu(count, ROW_DATIVE_FORMS)}`
}

export function formatCreateSourceRow(name: string, quantity: string, unit: string): string {
  const amount = [quantity, unit].filter(Boolean).join(' ')
  return amount ? `Строка из таблицы: «${name}», ${amount}` : `Строка из таблицы: «${name}»`
}

export function formatNameLength(length: number, limit: number): string {
  return `${length} / ${limit}`
}

export function formatItemGetsProductName(name: string): string {
  return `Позиция в заявке получит название товара: «${name}».`
}

export function formatChooseSimilarLabel(name: string): string {
  return `Выбрать «${name}»`
}

export function formatTableUnitNote(unitText: string): string {
  return `в таблице: ${unitText}`
}

export function formatExcludeRowLabel(name: string): string {
  return `Не добавлять строку «${name}»`
}

export function formatQuantityFieldLabel(name: string): string {
  return `Количество: ${name}`
}

export function formatUnitFieldLabel(name: string): string {
  return `Единица измерения: ${name}`
}

export function formatPriceFieldLabel(name: string): string {
  return `Цена: ${name}`
}

export function formatProductSearchLabel(name: string): string {
  return `Товар каталога для строки «${name}»`
}

export function formatImportedItemsBanner(count: number): string {
  const verb = pluralizeRu(count, ['Добавлена', 'Добавлены', 'Добавлено'])
  return `${verb} ${count} ${pluralizeRu(count, ITEM_FORMS)} из таблицы. Проверьте количество и цены перед отправкой.`
}
